import React from 'react';
import ErrorBoundary from '../ui/ErrorBoundary';

const DashboardLayout = ({ 
  children, 
  className = '',
  maxWidth = '7xl',
  padding = 'default',
  background = 'silver-mist',
  withErrorBoundary = true
}) => {
  const getPaddingClasses = (padding) => {
    switch (padding) {
      case 'none':
        return '';
      case 'small':
        return 'p-4 sm:p-6';
      case 'large':
        return 'p-6 sm:p-8 lg:p-12';
      case 'default':
      default:
        return 'p-4 sm:p-6 lg:p-8';
    }
  };

  const getMaxWidthClass = (maxWidth) => {
    const widthMap = {
      'xs': 'max-w-xs',
      'sm': 'max-w-sm',
      'md': 'max-w-md',
      'lg': 'max-w-lg',
      'xl': 'max-w-xl',
      '2xl': 'max-w-2xl',
      '3xl': 'max-w-3xl',
      '4xl': 'max-w-4xl',
      '5xl': 'max-w-5xl',
      '6xl': 'max-w-6xl',
      '7xl': 'max-w-7xl',
      'full': 'max-w-full',
      'none': ''
    };
    return widthMap[maxWidth] || 'max-w-7xl';
  };

  const layoutClasses = `
    min-h-screen bg-${background} transition-colors duration-300
    ${className}
  `.trim();

  const containerClasses = `
    ${getMaxWidthClass(maxWidth)} mx-auto ${getPaddingClasses(padding)}
    space-y-6 lg:space-y-8
  `.trim();

  const LayoutContent = () => (
    <div className={layoutClasses}>
      <div className={containerClasses}>
        <div className="space-y-6 lg:space-y-8">
          {children}
        </div>
      </div>
    </div>
  );

  if (withErrorBoundary) {
    return (
      <ErrorBoundary
        variant="full-screen"
        title="Dashboard Error"
        message="Something went wrong while loading the dashboard."
      >
        <LayoutContent />
      </ErrorBoundary>
    );
  }

  return <LayoutContent />;
};

// Preset layout variants for common use cases
export const CompactDashboardLayout = ({ children, className = '', ...props }) => (
  <DashboardLayout
    maxWidth="5xl"
    padding="small"
    className={`${className}`}
    {...props}
  >
    <div className="space-y-4 lg:space-y-6">
      {children}
    </div>
  </DashboardLayout>
);

export const WideDashboardLayout = ({ children, className = '', ...props }) => (
  <DashboardLayout
    maxWidth="full"
    padding="large"
    background="crystal-white"
    className={`border-t border-woodland-moss/10 ${className}`}
    {...props}
  >
    <div className="space-y-8 lg:space-y-12">
      {children}
    </div>
  </DashboardLayout>
);

export const CenteredDashboardLayout = ({ children, className = '', ...props }) => (
  <DashboardLayout
    maxWidth="4xl"
    className={`${className}`}
    {...props}
  >
    <div className="text-center space-y-6 lg:space-y-8">
      <div className="max-w-2xl mx-auto">
        {children}
      </div>
    </div>
  </DashboardLayout>
);

export default DashboardLayout;
